import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { useProject } from "@/context/ProjectContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RichTextEditor } from "@/components/RichTextEditor";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FILE_CATEGORIES } from "@/components/badges";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

export function CreateRequestDialog({ open, onOpenChange, parties }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { selectedId } = useProject();
  const [form, setForm] = useState({ target_party_id: "", title: "", description: "", category: "Other", due_date: "" });

  const createMut = useMutation({
    mutationFn: async () => (await api.post(`/projects/${selectedId}/file-requests`, form)).data,
    onSuccess: () => {
      toast.success(t("filesPage.toastSent"));
      onOpenChange(false);
      setForm({ target_party_id: "", title: "", description: "", category: "Other", due_date: "" });
      qc.invalidateQueries({ queryKey: ["file-requests", selectedId] });
      qc.invalidateQueries({ queryKey: ["documents", selectedId] });
      qc.invalidateQueries({ queryKey: ["dashboard", selectedId] });
    },
    onError: (e) => toast.error(fmtErr(e)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="create-request-dialog">
        <DialogHeader><DialogTitle>{t("filesPage.createTitle")}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>{t("filesPage.targetLabel")}</Label>
            <Select value={form.target_party_id} onValueChange={(v) => setForm({ ...form, target_party_id: v })}>
              <SelectTrigger data-testid="request-target-select"><SelectValue placeholder={t("common.selectParty")} /></SelectTrigger>
              <SelectContent>
                {parties.map((p) => (
                  <SelectItem key={p.id} value={p.id} data-testid={`request-target-${p.id}`}>{p.company_name} ({p.role})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>{t("common.title")}</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="request-title-input" />
          </div>
          <div className="space-y-1.5">
            <Label>{t("common.description")}</Label>
            <RichTextEditor value={form.description} onChange={(v) => setForm({ ...form, description: v })} testId="request-description-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>{t("common.category")}</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="request-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FILE_CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c} data-testid={`request-category-${c.replace(/\s+/g, "-")}`}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("common.dueDate")}</Label>
              <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="request-due-date-input" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={() => createMut.mutate()} disabled={!form.title.trim() || !form.target_party_id || !form.due_date || createMut.isPending} data-testid="create-request-submit-button">
            {createMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {t("filesPage.sendRequest")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
